import { useEffect, useState } from 'react';
import type { Memory, MemoryKind, Problem } from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Table } from '../components/Table';
import { usePlatform } from '../app/platform';
import { memoryKindView } from '../app/status';
import { toProblem, useAsync } from '../app/useAsync';
import { useIdempotencyKey } from '../app/useIdempotencyKey';
import { useStudioEvents } from '../app/useStudioEvents';
import { InlineMarkdown } from '../lib/markdown';

const MEMORY_KINDS: readonly MemoryKind[] = ['requirement', 'decision', 'constraint', 'preference'];
const REFERENCED_MESSAGE = '参照している要素があるので削除できません。退役してください。';

/**
 * 記憶（1 件 = 1 つの要求・決定・制約・好み）。回答から基盤が自動で、Agent が memory_remember で、利用者がここで作る。
 * active なものを毎ターン AI に渡す。退役は削除ではなく出典の参照を残す。削除は参照が無いときだけ。
 */
export function MemoryPanel({ workspaceId }: { workspaceId: string }) {
  const { api } = usePlatform();
  // PoC は 1 ページ 100 件まで（続きは残課題）。
  const memories = useAsync(() => api.listMemories(workspaceId, { limit: 100 }), [api, workspaceId]);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [deleting, setDeleting] = useState<Memory | null>(null);

  const cursor = memories.data?.eventCursor ?? null;
  const streamState = useStudioEvents({
    api,
    workspaceId,
    cursor,
    enabled: cursor !== null,
    onEvent: (event) => {
      if (event.type === 'studio.knowledge' && event.action?.startsWith('memory_')) memories.reload();
    },
  });
  useEffect(() => {
    if (streamState === 'expired') memories.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamState]);

  const setStatus = async (memory: Memory, status: 'active' | 'retired') => {
    setProblem(null);
    try {
      await api.updateMemory(workspaceId, memory.id, { expectedVersion: memory.version, status });
      memories.reload();
    } catch (error) {
      setProblem(toProblem(error));
    }
  };

  return (
    <Card title="記憶">
      <div className="stack">
        <p className="hint">会話で決まった要求・決定・制約・好みです。有効なものを AI が毎回読みます。回答した質問は自動で決定になります。</p>
        <AddMemoryForm workspaceId={workspaceId} onAdded={memories.reload} />
        {memories.loading && memories.data === null && <Spinner label="記憶を取得しています" />}
        <ProblemNotice problem={memories.problem ?? problem} action={<Button onClick={memories.reload}>再取得</Button>} />
        {memories.data !== null && memories.data.items.length === 0 && <EmptyState title="記憶はまだありません" />}
        {memories.data !== null && memories.data.items.length > 0 && (
          <Table
            label="記憶"
            columns={[
              { key: 'name', header: '名前' },
              { key: 'text', header: '内容' },
              { key: 'kind', header: '種類' },
              { key: 'author', header: '作者' },
              { key: 'status', header: '状態' },
              { key: 'refs', header: '参照' },
              { key: 'actions', header: '操作' },
            ]}
            rows={memories.data.items.map((memory) => ({
              key: memory.id,
              cells: {
                name: <code>{memory.name}</code>,
                text: <InlineMarkdown text={memory.text} />,
                kind: memoryKindView(memory.kind),
                author: memory.author === 'user' ? '利用者' : 'AI',
                status:
                  memory.status === 'active' ? <Badge tone="completed">有効</Badge> : <Badge tone="cancelled">退役</Badge>,
                refs: memory.refCount,
                actions: (
                  <span className="row">
                    {memory.status === 'active' ? (
                      <Button variant="text" aria-label={`${memory.name} を退役する`} onClick={() => void setStatus(memory, 'retired')}>
                        退役する
                      </Button>
                    ) : (
                      <Button variant="text" aria-label={`${memory.name} を戻す`} onClick={() => void setStatus(memory, 'active')}>
                        戻す
                      </Button>
                    )}
                    <Button variant="text" aria-label={`${memory.name} を削除`} onClick={() => setDeleting(memory)}>
                      削除
                    </Button>
                  </span>
                ),
              },
            }))}
          />
        )}
        {deleting && (
          <ConfirmDialog
            title={`記憶「${deleting.name}」を削除`}
            body={<p>参照している要素が無いときだけ削除できます。出典に使われている記憶は退役してください。</p>}
            confirmLabel="削除する"
            describeFailure={(failure) => (failure.code === 'item_referenced' ? REFERENCED_MESSAGE : null)}
            onConfirm={async (idempotencyKey) => {
              await api.deleteMemory(workspaceId, deleting.id, idempotencyKey);
              setDeleting(null);
              memories.reload();
            }}
            onClose={() => setDeleting(null)}
          />
        )}
      </div>
    </Card>
  );
}

function AddMemoryForm({ workspaceId, onAdded }: { workspaceId: string; onAdded(): void }) {
  const { api } = usePlatform();
  const [text, setText] = useState('');
  const [kind, setKind] = useState<MemoryKind>('requirement');
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  // 同じ内容の押し直しには同じ冪等キー（C37）。応答だけを落とした再送で同じ記憶が 2 件できない。
  const idempotency = useIdempotencyKey();

  const submit = async () => {
    const value = text.trim();
    if (value === '' || submitting) return;
    setSubmitting(true);
    setProblem(null);
    try {
      await api.createMemory(workspaceId, { text: value, kind }, idempotency.keyFor(JSON.stringify({ text: value, kind })));
      idempotency.done();
      setText('');
      onAdded();
    } catch (error) {
      // 禁止情報（422 memory_rejected）や長すぎる内容（422 invalid_memory）は理由をそのまま出す。
      setProblem(toProblem(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="stack"
      aria-label="記憶を追加"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <textarea
        className="field-input"
        aria-label="記憶の内容"
        placeholder="例：インターンの受入確認は人事チームが担当する"
        maxLength={500}
        rows={2}
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      <div className="row">
        <select className="input" aria-label="種類" value={kind} onChange={(event) => setKind(event.target.value as MemoryKind)}>
          {MEMORY_KINDS.map((value) => (
            <option key={value} value={value}>
              {memoryKindView(value)}
            </option>
          ))}
        </select>
        <Button type="submit" variant="primary" disabled={submitting || text.trim() === ''}>
          {submitting ? '追加中…' : '追加する'}
        </Button>
      </div>
      <ProblemNotice problem={problem} />
    </form>
  );
}
